import { getJson } from "./http";
import type { TimeRange, TrafficMetrics, ErrorMetrics, InsightMetrics, SystemMetrics, ServiceStatus, LogRecord, ReleaseEntry, DeploymentRecord, UsageMetrics } from "@shared/types";

/**
 * A metrics window: a `range` preset, optionally narrowed to an absolute
 * from/to (epoch ms, #228 Phase 3). When from/to are present the backend resolves
 * an adaptive bucket and applies an upper bound; otherwise the preset is used.
 */
export interface MetricsArgs { range: TimeRange; from?: number; to?: number; tenant?: string; }

function metricsQs({ range, from, to, tenant }: MetricsArgs): string {
  const parts = [`range=${range}`];
  if (from !== undefined && to !== undefined) { parts.push(`from=${from}`, `to=${to}`); }
  if (tenant !== undefined) parts.push(`tenant=${encodeURIComponent(tenant)}`);
  return parts.join("&");
}

export function trafficUrl(args: MetricsArgs): string { return `/api/admin/metrics/traffic?${metricsQs(args)}`; }
export function errorsUrl(args: MetricsArgs): string { return `/api/admin/metrics/errors?${metricsQs(args)}`; }
export function insightsUrl(args: MetricsArgs): string { return `/api/admin/metrics/insights?${metricsQs(args)}`; }
export function usageUrl(args: MetricsArgs): string { return `/api/admin/metrics/usage?${metricsQs(args)}`; }

export const adminApi = {
  traffic: (args: MetricsArgs): Promise<TrafficMetrics> => getJson(trafficUrl(args)),
  errors: (args: MetricsArgs): Promise<ErrorMetrics> => getJson(errorsUrl(args)),
  insights: (args: MetricsArgs): Promise<InsightMetrics> => getJson(insightsUrl(args)),
  usage: (args: MetricsArgs): Promise<UsageMetrics> => getJson(usageUrl(args)),
  system: (): Promise<SystemMetrics> => getJson(`/api/admin/metrics/system`),
  services: (): Promise<ServiceStatus[]> => getJson(`/api/admin/metrics/services`),
  logs: (level?: number, limit = 200): Promise<{ logs: LogRecord[] }> =>
    getJson(`/api/admin/logs?limit=${limit}${level !== undefined ? `&level=${level}` : ""}`),
  releases: (): Promise<{ releases: ReleaseEntry[] }> => getJson(`/api/admin/releases`),
  deployments: (): Promise<{ deployments: DeploymentRecord[] }> => getJson(`/api/admin/deployments`)
};
